import { useState, useEffect } from 'react'
import { fmtDate } from '../../utils/format'
import DateInput from '../../components/DateInput'
import {
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  ClockIcon,
  CheckCircleIcon,
  CalendarIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline'

interface EquipmentMaintenance {
  id: number
  refrigerator_id: number
  refrigerator: {
    id: number
    name: string
    code: string
    zone?: {
      id: number
      name: string
      code: string
    }
  }
  type: string
  description: string
  issues_found?: string
  solutions_applied?: string
  parts_replaced?: string
  cost?: number
  technician_name?: string
  technician_company?: string
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
  scheduled_date: string
  started_at?: string
  completed_at?: string
  notes?: string
  attachments?: any[]
  created_at: string
  updated_at: string
}

interface MaintenanceType {
  value: string
  label: string
}

interface Refrigerator {
  id: number
  name: string
  code: string
}

const API_BASE_URL = '/api/v1/dev'

export default function Maintenance() {
  const [maintenances, setMaintenances] = useState<EquipmentMaintenance[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedMaintenance, setSelectedMaintenance] = useState<EquipmentMaintenance | null>(null)
  const [editingMaintenance, setEditingMaintenance] = useState<EquipmentMaintenance | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [refrigeratorFilter, setRefrigeratorFilter] = useState<string>('all')

  // Options
  const [maintenanceTypes, setMaintenanceTypes] = useState<MaintenanceType[]>([])
  const [refrigerators, setRefrigerators] = useState<Refrigerator[]>([])

  // Form data
  const [formData, setFormData] = useState({
    refrigerator_id: '',
    type: '',
    description: '',
    issues_found: '',
    solutions_applied: '',
    parts_replaced: '',
    cost: '',
    technician_name: '',
    technician_company: '',
    status: 'completed',
    scheduled_date: new Date().toISOString().split('T')[0],
    started_at: new Date().toISOString().split('T')[0],
    completed_at: new Date().toISOString().split('T')[0],
    notes: ''
  })

  // Fetch maintenances
  const fetchMaintenances = async () => {
    try {
      setLoading(true)
      setError(null)
      let url = `${API_BASE_URL}/equipment-maintenances`
      const params = new URLSearchParams()

      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (typeFilter !== 'all') params.append('type', typeFilter)
      if (refrigeratorFilter !== 'all') params.append('refrigerator_id', refrigeratorFilter)

      if (params.toString()) url += `?${params.toString()}`

      const response = await fetch(url)
      const data = await response.json()

      if (response.ok) {
        setMaintenances(data.data || [])
      } else {
        setError('Error al cargar mantenimientos')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error fetching maintenances:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch options
  const fetchOptions = async () => {
    try {
      // Fetch maintenance types
      const typesResponse = await fetch(`${API_BASE_URL}/equipment-maintenances/types`)
      const typesData = await typesResponse.json()
      if (typesResponse.ok) {
        setMaintenanceTypes(typesData.data || [])
      }

      // Fetch refrigerators
      const refResponse = await fetch(`${API_BASE_URL}/refrigerators`)
      const refData = await refResponse.json()
      if (refResponse.ok) {
        setRefrigerators(refData.data || [])
      }
    } catch (err) {
      console.error('Error fetching options:', err)
    }
  }

  useEffect(() => {
    fetchMaintenances()
    fetchOptions()
  }, [statusFilter, typeFilter, refrigeratorFilter])

  // Translate maintenance type
  const getTypeLabel = (type: string) => {
    const typeLabels: Record<string, string> = {
      preventivo: 'Preventivo',
      correctivo: 'Correctivo',
      emergencia: 'Emergencia',
      rutinario: 'Rutinario',
    }
    return typeLabels[type] || type
  }

  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      scheduled: { color: 'bg-blue-50 text-blue-700 border border-blue-200', icon: CalendarIcon, text: 'Programado' },
      in_progress: { color: 'bg-yellow-50 text-yellow-700 border border-yellow-200', icon: ClockIcon, text: 'En Progreso' },
      completed: { color: 'bg-green-50 text-green-700 border border-green-200', icon: CheckCircleIcon, text: 'Completado' },
      cancelled: { color: 'bg-red-50 text-red-700 border border-red-200', icon: XMarkIcon, text: 'Cancelado' }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.scheduled
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    )
  }

  // Handle create or update
  const handleSave = async () => {
    setSaving(true)
    setError(null)

    try {
      const url = editingMaintenance
        ? `${API_BASE_URL}/equipment-maintenances/${editingMaintenance.id}`
        : `${API_BASE_URL}/equipment-maintenances`

      const method = editingMaintenance ? 'PUT' : 'POST'

      const payload = {
        ...formData,
        refrigerator_id: parseInt(formData.refrigerator_id),
        cost: formData.cost ? parseFloat(formData.cost) : null,
        scheduled_date: formData.scheduled_date || null,
        started_at: formData.started_at || null,
        completed_at: formData.completed_at || null
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (response.ok) {
        await fetchMaintenances()
        setShowCreateModal(false)
        setEditingMaintenance(null)
        resetForm()
      } else {
        if (data.errors) {
          const messages = Object.values(data.errors).flat().join('\n')
          setError(messages)
        } else {
          setError(data.message || 'Error al guardar el mantenimiento')
        }
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error saving maintenance:', err)
    } finally {
      setSaving(false)
    }
  }

  // Delete maintenance
  const deleteMaintenance = async (id: number) => {
    if (!confirm('¿Está seguro de eliminar este mantenimiento?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/equipment-maintenances/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        await fetchMaintenances()
      } else {
        const data = await response.json()
        setError(data.message || 'Error al eliminar el mantenimiento')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error deleting maintenance:', err)
    }
  }

  // View maintenance details
  const viewMaintenance = (maintenance: EquipmentMaintenance) => {
    setSelectedMaintenance(maintenance)
    setShowViewModal(true)
  }

  // Open create modal
  const openCreateModal = () => {
    setEditingMaintenance(null)
    resetForm()
    setShowCreateModal(true)
    setError(null)
  }

  // Edit maintenance
  const editMaintenance = (maintenance: EquipmentMaintenance) => {
    setEditingMaintenance(maintenance)
    setFormData({
      refrigerator_id: maintenance.refrigerator_id.toString(),
      type: maintenance.type,
      description: maintenance.description,
      issues_found: maintenance.issues_found || '',
      solutions_applied: maintenance.solutions_applied || '',
      parts_replaced: maintenance.parts_replaced || '',
      cost: maintenance.cost?.toString() || '',
      technician_name: maintenance.technician_name || '',
      technician_company: maintenance.technician_company || '',
      status: maintenance.status,
      scheduled_date: maintenance.scheduled_date ? maintenance.scheduled_date.split('T')[0] : '',
      started_at: maintenance.started_at ? maintenance.started_at.split('T')[0] : '',
      completed_at: maintenance.completed_at ? maintenance.completed_at.split('T')[0] : '',
      notes: maintenance.notes || ''
    })
    setShowCreateModal(true)
    setError(null)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      refrigerator_id: '',
      type: '',
      description: '',
      issues_found: '',
      solutions_applied: '',
      parts_replaced: '',
      cost: '',
      technician_name: '',
      technician_company: '',
      status: 'completed',
      scheduled_date: new Date().toISOString().split('T')[0],
      started_at: new Date().toISOString().split('T')[0],
      completed_at: new Date().toISOString().split('T')[0],
      notes: ''
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mantenimiento de Equipos</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestiona los mantenimientos de los equipos de refrigeración
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Nuevo Mantenimiento
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="all">Todos</option>
              <option value="scheduled">Programado</option>
              <option value="in_progress">En Progreso</option>
              <option value="completed">Completado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Tipo</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="all">Todos</option>
              {maintenanceTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Equipo</label>
            <select
              value={refrigeratorFilter}
              onChange={(e) => setRefrigeratorFilter(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="all">Todos</option>
              {refrigerators.map((ref) => (
                <option key={ref.id} value={ref.id}>
                  {ref.name} ({ref.code})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Maintenances Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      ) : maintenances.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay mantenimientos</h3>
          <p className="mt-1 text-sm text-gray-500">Comienza programando un nuevo mantenimiento.</p>
          <div className="mt-6">
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Nuevo Mantenimiento
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Equipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha Programada
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Técnico
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {maintenances.map((maintenance) => (
                <tr key={maintenance.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {maintenance.refrigerator.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {maintenance.refrigerator.code}
                        {maintenance.refrigerator.zone && (
                          <span className="ml-2">• {maintenance.refrigerator.zone.name}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {getTypeLabel(maintenance.type)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {fmtDate(maintenance.scheduled_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {maintenance.technician_name || '-'}
                    </div>
                    {maintenance.technician_company && (
                      <div className="text-xs text-gray-500">
                        {maintenance.technician_company}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(maintenance.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => viewMaintenance(maintenance)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Ver detalles"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => editMaintenance(maintenance)}
                        className="text-green-600 hover:text-green-900"
                        title="Editar"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => deleteMaintenance(maintenance.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-4xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingMaintenance ? 'Editar Mantenimiento' : 'Nuevo Mantenimiento'}
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Equipo *
                      </label>
                      <select
                        value={formData.refrigerator_id}
                        onChange={(e) => setFormData({ ...formData, refrigerator_id: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      >
                        <option value="">Selecciona un equipo</option>
                        {refrigerators.map((ref) => (
                          <option key={ref.id} value={ref.id}>
                            {ref.name} ({ref.code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Tipo *
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      >
                        <option value="">Selecciona un tipo</option>
                        {maintenanceTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Descripción *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Describe el mantenimiento a realizar..."
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Estado *
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      >
                        <option value="scheduled">Programado</option>
                        <option value="in_progress">En Progreso</option>
                        <option value="completed">Completado</option>
                        <option value="cancelled">Cancelado</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fecha Programada *
                      </label>
                      <DateInput
                        value={formData.scheduled_date}
                        onChange={(v) => setFormData({ ...formData, scheduled_date: v })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fecha de Inicio
                      </label>
                      <DateInput
                        value={formData.started_at}
                        onChange={(v) => setFormData({ ...formData, started_at: v })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fecha de Finalización
                      </label>
                      <DateInput
                        value={formData.completed_at}
                        onChange={(v) => setFormData({ ...formData, completed_at: v })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Técnico
                      </label>
                      <input
                        type="text"
                        value={formData.technician_name}
                        onChange={(e) => setFormData({ ...formData, technician_name: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        placeholder="Nombre del técnico"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Empresa
                      </label>
                      <input
                        type="text"
                        value={formData.technician_company}
                        onChange={(e) => setFormData({ ...formData, technician_company: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        placeholder="Empresa del técnico"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Costo
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.cost}
                        onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                        className="mt-1 block w-full pl-10 border-gray-300 rounded-md shadow-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Incidencias Encontradas
                    </label>
                    <textarea
                      value={formData.issues_found}
                      onChange={(e) => setFormData({ ...formData, issues_found: e.target.value })}
                      rows={2}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Describe las incidencias encontradas..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Soluciones Aplicadas
                    </label>
                    <textarea
                      value={formData.solutions_applied}
                      onChange={(e) => setFormData({ ...formData, solutions_applied: e.target.value })}
                      rows={2}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Describe las soluciones aplicadas..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Piezas Reemplazadas
                    </label>
                    <textarea
                      value={formData.parts_replaced}
                      onChange={(e) => setFormData({ ...formData, parts_replaced: e.target.value })}
                      rows={2}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Lista las piezas reemplazadas..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Notas Adicionales
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Notas adicionales..."
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {saving ? 'Guardando...' : (editingMaintenance ? 'Actualizar' : 'Crear')}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedMaintenance && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-3xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Detalles del Mantenimiento
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Equipo</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedMaintenance.refrigerator.name} ({selectedMaintenance.refrigerator.code})
                      </p>
                      {selectedMaintenance.refrigerator.zone && (
                        <p className="text-xs text-gray-500">
                          Zona: {selectedMaintenance.refrigerator.zone.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Tipo</p>
                      <p className="mt-1 text-sm text-gray-900">{getTypeLabel(selectedMaintenance.type)}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">Descripción</p>
                    <p className="mt-1 text-sm text-gray-900">{selectedMaintenance.description}</p>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Estado</p>
                      <div className="mt-1">
                        {getStatusBadge(selectedMaintenance.status)}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Fecha Programada</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {fmtDate(selectedMaintenance.scheduled_date)}
                      </p>
                    </div>
                    {selectedMaintenance.cost && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Costo</p>
                        <p className="mt-1 text-sm text-gray-900">
                          ${parseFloat(String(selectedMaintenance.cost)).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>

                  {(selectedMaintenance.started_at || selectedMaintenance.completed_at) && (
                    <div className="grid grid-cols-2 gap-4">
                      {selectedMaintenance.started_at && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Fecha de Inicio</p>
                          <p className="mt-1 text-sm text-gray-900">
                            {fmtDate(selectedMaintenance.started_at)}
                          </p>
                        </div>
                      )}
                      {selectedMaintenance.completed_at && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Fecha de Finalización</p>
                          <p className="mt-1 text-sm text-gray-900">
                            {fmtDate(selectedMaintenance.completed_at)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {(selectedMaintenance.technician_name || selectedMaintenance.technician_company) && (
                    <div className="grid grid-cols-2 gap-4">
                      {selectedMaintenance.technician_name && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Técnico</p>
                          <p className="mt-1 text-sm text-gray-900">{selectedMaintenance.technician_name}</p>
                        </div>
                      )}
                      {selectedMaintenance.technician_company && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">Empresa</p>
                          <p className="mt-1 text-sm text-gray-900">{selectedMaintenance.technician_company}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedMaintenance.issues_found && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Incidencias Encontradas</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedMaintenance.issues_found}</p>
                    </div>
                  )}

                  {selectedMaintenance.solutions_applied && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Soluciones Aplicadas</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedMaintenance.solutions_applied}</p>
                    </div>
                  )}

                  {selectedMaintenance.parts_replaced && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Piezas Reemplazadas</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedMaintenance.parts_replaced}</p>
                    </div>
                  )}

                  {selectedMaintenance.notes && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Notas</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedMaintenance.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}